import { inject, Injectable } from '@angular/core';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { Store } from '@skyeng/libs/shared/store';
import { FileUploadApiService } from './file-upload-api.service';
import { ISkyApiUploadHandlers } from '@skyeng/libs/shared/api';
import { Attachment, AttachmentFileType, AttachmentUpload, UploadProgress } from '../interface';
import { AttachmentStatus, getMessageLocalStorageKey, MAX_FILE_SIZE_MB, MAX_FILES_COUNT } from '../const';
import { v4 } from 'uuid';
import { NEVER } from 'rxjs';
import { DsNotificationService } from '@skyeng/libs/shared/ui-kit-v2/notification';
import debounce from 'lodash/debounce';
import { SkyLocalStorageService } from '@skyeng/libs/shared/local-storage';
import { TranslocoService } from '@ngneat/transloco';

const STORE_INITAIL_STATE = {
  attachmentsUpload: [] as AttachmentUpload[],
  message: '',
  localStorageKey: null as string | null,
};

interface StoreEvents {
  progress: UploadProgress;
}

@Injectable()
export class MessageRequestService {
  private store = new Store<typeof STORE_INITAIL_STATE, StoreEvents>(STORE_INITAIL_STATE);

  attachmentsUpload$ = this.store.select('attachmentsUpload');
  progress$ = this.store.on('progress');
  message$ = this.store.select('message');

  saveText = debounce(this.saveTextToStorage, 500);

  private dsNotificationService = inject(DsNotificationService);
  private fileUploadApiService = inject(FileUploadApiService);
  private skyLocalStorageService = inject(SkyLocalStorageService);
  private translocoService = inject(TranslocoService);

  private getLocalStorageKey(): string {
    const lsKey = this.store.get('localStorageKey');
    return lsKey ? lsKey : getMessageLocalStorageKey();
  }

  initMessage(loadTextFromStorage: boolean, localStorageKey: string | null = null): void {
    this.store.updateField('localStorageKey', localStorageKey);
    this.setMessage((loadTextFromStorage && this.getSavedText()) || '', false, localStorageKey);
  }

  getValidFiles(files: File[], allowedTypes: string[]): File[] {
    const validFiles = [...files].filter(file => {
      const fileType = file.type; // MIME-тип файла, например, "image/png" или "application/pdf"

      const fileNameParts = file.name.split('.');
      const fileExtension = '.' + [...fileNameParts].pop(); // Расширение файла с точкой, например ".png"

      return allowedTypes.some(type => {
        if (type.includes('/*')) {
          // Обработка масок, например "image/*"
          const baseType = type.split('/')[0];
          return fileType.startsWith(baseType + '/');
        } else if (type.startsWith('.')) {
          // Обработка расширений, например ".txt" или ".pdf"
          return fileExtension === type;
        } else {
          // Обработка точных MIME-типов
          return fileType === type;
        }
      });
    });

    if (validFiles.length !== files.length) {
      this.dsNotificationService.openErrorNotification({
        content: `Некоторые файлы не удалось загрузить. Прикрепить можно только файлы ${allowedTypes.join(', ')}`,
        autoClosing: true,
      });
    }

    return validFiles;
  }

  addAttachments(files: File[]): void {
    if (this.store.get('attachmentsUpload').length + files.length > MAX_FILES_COUNT) {
      this.dsNotificationService.openErrorNotification({
        content: this.translocoService.translate('skyproLibMessages.maxFileCountError', { MAX_FILES_COUNT }),
        autoClosing: true,
      });

      return;
    }

    files.forEach(file => this.addAttachment(file));
  }

  removeAttachment(attachmentUuid: string): void {
    const attachments = this.store.get('attachmentsUpload').filter(attachment => attachment.uuid !== attachmentUuid);
    this.store.updateField('attachmentsUpload', attachments);
  }

  getAttachments(): Attachment[] {
    return this.store
      .get('attachmentsUpload')
      .map(({ url, title }) => ({ url, title }))
      .filter((attachment): attachment is Attachment => attachment.url !== undefined);
  }

  getAttachmentsUpload(): AttachmentUpload[] {
    return this.store.get('attachmentsUpload');
  }

  setMessage(message: string, saveInProgressText: boolean, localStorageKey: null | string): void {
    this.store.updateFields({
      message,
      localStorageKey,
    });

    if (saveInProgressText) {
      this.saveText(message);
    }
  }

  getMessage(): string {
    return this.store.get('message');
  }

  /**
   * Сбросить сообщение и атачи
   */
  clear(): void {
    this.store.updateFields({
      message: '',
      attachmentsUpload: [],
    });
    this.removeSavedText();
  }

  private addAttachment(file: File): void {
    const type = this.getAttachmentType(file.type);

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      // Предел ловит любой файл, а не только видео: «сожмите видео» поверх архива или pdf —
      // инструкция, которую нельзя выполнить, поэтому совет ветвится по типу.
      const key =
        type === AttachmentFileType.video ? 'skyproLibMessages.videoIsTooBig' : 'skyproLibMessages.fileIsTooBig';
      this.dsNotificationService.openErrorNotification({
        content: this.translocoService.translate(key, {
          MAX_FILE_SIZE_MB,
          name: file.name,
        }),
        autoClosing: true,
      });
      return;
    }

    const attachmentUpload: AttachmentUpload = {
      uuid: v4(),
      mimeType: file.type,
      type,
      title: file.name,
      previewUrl: [AttachmentFileType.image, AttachmentFileType.video].includes(type)
        ? this.getPreviewUrl(file)
        : undefined,
      status: AttachmentStatus.uploading,
    };

    const attachmentsUpload = this.store.get('attachmentsUpload');
    this.store.updateField('attachmentsUpload', [...attachmentsUpload, attachmentUpload]);

    const uploadEventHandlers: ISkyApiUploadHandlers = {
      progress: progress => {
        const uploadProgress = progress.total ? Math.round((progress.loaded / progress.total) * 100) : 50;
        this.fireProgress(attachmentUpload.uuid, uploadProgress);
      },
      error: () => {
        this.updateAttachment(attachmentUpload.uuid, {
          status: AttachmentStatus.error,
        });
      },
    };

    const attachmentRemoved$ = this.attachmentsUpload$.pipe(
      filter(attachments => !attachments.some(({ uuid }) => attachmentUpload.uuid === uuid)),
    );

    this.fileUploadApiService
      .upload(file, uploadEventHandlers)
      .pipe(
        catchError(error => {
          console.error(error);
          this.updateAttachment(attachmentUpload.uuid, {
            status: AttachmentStatus.error,
          });

          return NEVER;
        }),
        takeUntil(attachmentRemoved$),
      )
      .subscribe(uploadedFile => {
        this.fireProgress(attachmentUpload.uuid, 100);
        this.updateAttachment(attachmentUpload.uuid, {
          status: AttachmentStatus.uploaded,
          url: encodeURI(uploadedFile.url),
        });
      });
  }

  private getAttachmentType(type: string): AttachmentFileType {
    if (type.match('image')) {
      return AttachmentFileType.image;
    }
    if (type.match('video')) {
      return AttachmentFileType.video;
    }
    return AttachmentFileType.other;
  }

  private updateAttachment(uuid: string, fields: Partial<AttachmentUpload>): void {
    const attachmentsUpload = [...this.store.get('attachmentsUpload')];
    const index = attachmentsUpload.findIndex(attachment => attachment.uuid === uuid);

    if (index === -1) {
      return;
    }

    // eslint-disable-next-line functional/immutable-data
    attachmentsUpload[index] = {
      ...attachmentsUpload[index],
      ...fields,
    };

    this.store.updateFields({ attachmentsUpload });
  }

  private fireProgress(uuid: string, value: number): void {
    this.store.fire('progress', { uuid, value });
  }

  setAttachments(attachments: Attachment[]): void {
    this.store.updateFields({
      attachmentsUpload: attachments.map(attachment => {
        const attachmentUpload: AttachmentUpload = {
          uuid: v4(),
          mimeType: attachment.mimeType,
          title: attachment.title,
          url: attachment.url,
          status: AttachmentStatus.uploaded,
        };
        return attachmentUpload;
      }),
    });
  }

  saveTextToStorage(text: string): void {
    this.skyLocalStorageService.set(this.getLocalStorageKey(), text);
  }

  getSavedText(): string | null {
    return this.skyLocalStorageService.get(this.getLocalStorageKey());
  }

  removeSavedText(): void {
    this.skyLocalStorageService.remove(this.getLocalStorageKey());
    this.store.updateField('localStorageKey', null);
  }

  private getPreviewUrl(file: File): string {
    return URL.createObjectURL(file);
  }
}
